import { IHealthcarepackage } from 'src/app/core/models/Healthcarepackage';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PERIOD, TransactionTarget, TransactionType } from 'src/app/core/enums/enums';
import { Currency } from 'src/app/core/models/Common';
import { Wallet } from 'src/app/core/models/Group';
import { ITransaction } from 'src/app/core/models/Transaction';
import { IUser } from 'src/app/core/models/User';
import { CurrencyService, UseCaseGroupService } from 'src/app/core/rest-services/rest-services';
import { ServicesService } from 'src/app/core/services/services.service';
import { SessionService } from 'src/app/core/services/session.service';
import { toLocaleDateTime } from 'src/app/core/utils/datetime';
import { toHealthCredit, toMoney } from 'src/app/core/utils/misc';
import { OptionObject } from 'src/app/core/utils/type';
import { TopUpComponent } from 'src/app/shared-components/top-up/top-up.component';
import { DiscountComponent } from 'src/app/shared-components/discount/discount.component';
import { makeCurrencytoShow } from 'src/app/core/utils/functions';
import { CustomRangeCalendarComponent } from '../custom-range-calendar/custom-range-calendar.component';
import { DateRange } from '@angular/material/datepicker';
import * as moment from 'moment';

@Component({
  selector: 'app-transaction',
  templateUrl: './transaction.component.html',
  styleUrls: ['./transaction.component.scss'],
})
export class TransactionComponent implements OnInit, OnDestroy {
  optionPeriodDropdown!: OptionObject[];
  translations: any = {
    thisMonth: '',
    thisYear: '',
    thisDay: '',
    thisWeek: '',
    custom: '',
  };
  translationKeys: string[] = [
    'admin-fields.year',
    'admin-fields.month',
    'admin-fields.week',
    'admin-fields.day',
    'admin-fields.custom-period',
  ];
  isTransitioning = false;
  wallet!: Wallet;
  currency!: Currency;
  language: string = '';
  transactions: ITransaction[] = [];
  currencies: Currency[] = [];
  avatar: any;
  transactionType = TransactionType;
  transactionTarget = TransactionTarget;
  $languageChange!: Subscription;
  $usedCredits!: Subscription;
  $userUpdate!: Subscription;
  filterPickedValue?: OptionObject;
  startDate!: Date;
  endDate!: Date;
  period = PERIOD.MONTH;
  startDateToDisplay = '';
  endDateToDisplay = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { caller: string },
    public dialogRef: MatDialogRef<TransactionComponent>,
    private translateService: TranslateService,
    private usecaseGroupService: UseCaseGroupService,
    private sessionService: SessionService,
    private services: ServicesService,
    private currencyService: CurrencyService,
  ) {}

  ngOnDestroy(): void {
    if (this.$languageChange) this.$languageChange.unsubscribe();
    if (this.$usedCredits) this.$usedCredits.unsubscribe();
    if (this.$userUpdate) this.$userUpdate.unsubscribe();
  }

  ngOnInit(): void {
    this.listenForLanguageChangeEvents();
    this.getTranslations();
    this.currency = this.sessionService.getCurrencie();
    this.avatar = this.sessionService.getUser().image_profil;
    this.language = this.translateService.currentLang;
    this.fetchCurrencies();
    this.fetchWallet();
    this.$usedCredits = this.services.eventService.subscribe('healthcredit:used', () => {
      this.fetchWallet();
      this.fetchTransaction();
    });
    this.$userUpdate = this.services.eventService.subscribe('user:update', (user: IUser) => {
      this.currency = this.sessionService.getCurrencie();
    });
  }

  fetchWallet(): void {
    this.isTransitioning = true;
    let groupId = this.sessionService.getCurrentGroupId();
    this.usecaseGroupService.wallet(groupId).subscribe({
      next: (res: Wallet) => {
        this.wallet = res;
      },
      error: (error) => {
        error = this.translateService.instant('errors.generic');
      },
      complete: () => {
        this.isTransitioning = false;
      },
    });
  }

  fetchCurrencies(): void {
    this.isTransitioning = true;
    this.currencyService.index().subscribe({
      next: (res: Currency[]) => {
        this.currencies = res;
      },
      error: (error: any) => {
        console.log('Could not get currencies');
      },
      complete: () => {
        this.isTransitioning = false;
      },
    });
  }

  fetchTransaction(): void {
    this.isTransitioning = true;
    let groupId = this.sessionService.getCurrentGroupId();
    this.usecaseGroupService
      .list_transactions(
        groupId,
        this.period,
        this.period === PERIOD.CUSTOM ? moment(this.startDate).format('YYYY-MM-DD') : undefined,
        this.period === PERIOD.CUSTOM ? moment(this.endDate).format('YYYY-MM-DD') : undefined,
      )
      .subscribe({
        next: (res: ITransaction[]) => {
          this.transactions = res;
        },
        error: (error) => {
          error = this.translateService.instant('errors.generic');
        },
        complete: () => {
          this.isTransitioning = false;
        },
      });
  }

  openTopUpModal(): void {
    this.services.modalService
      .openModal(TopUpComponent, {
        width: '900px',
        height: 'auto',
        data: {
          currencies: this.currencies,
        },
      })
      .subscribe(() => {});
  }

  toLocaleDateTime(date: string): string {
    return toLocaleDateTime(new Date(date), this.language);
  }

  getFilter(): void {
    if (this.period === PERIOD.CUSTOM) {
      this.optionPeriodDropdown = [
        {
          label: this.translations.custom,
          value: PERIOD.CUSTOM,
        },
        {
          label: this.translations.thisWeek,
          value: PERIOD.WEEK,
        },
        {
          label: this.translations.thisMonth,
          value: PERIOD.MONTH,
        },
        {
          label: this.translations.thisYear,
          value: PERIOD.YEAR,
        },
      ];
    } else {
      this.optionPeriodDropdown = [
        {
          label: this.translations.thisWeek,
          value: PERIOD.WEEK,
        },
        {
          label: this.translations.thisMonth,
          value: PERIOD.MONTH,
        },
        {
          label: this.translations.thisYear,
          value: PERIOD.YEAR,
        },
        {
          label: this.translations.custom,
          value: PERIOD.CUSTOM,
        },
      ];
    }
  }

  handleCustomPeriod(): void {
    this.services.modalService
      .openModal(CustomRangeCalendarComponent, {
        width: '500px',
        height: 'auto',
        data: {
          incomingDateRange: new DateRange<Date>(this.startDate, this.endDate),
        },
      })
      .subscribe((range: DateRange<Date>) => {
        if (range) {
          this.period = PERIOD.CUSTOM;
          this.filterPickedValue = {
            label: this.translations.custom,
            value: PERIOD.CUSTOM,
          };
          this.getFilter();
          this.startDate = new Date(moment(range.start).format('MM/DD/YYYY'));
          this.endDate = new Date(moment(range.end).format('MM/DD/YYYY'));
          this.startDateToDisplay = moment(range.start).format('MM/DD/YYYY');
          this.endDateToDisplay = moment(range.end).format('MM/DD/YYYY');
          this.fetchTransaction();
        }
      });
  }

  handlePeriodDropdownEvent(period: any): void {
    if (period !== 'open') {
      this.period = period.value as PERIOD;
      if (this.period === PERIOD.CUSTOM) {
        this.services.modalService
          .openModal(CustomRangeCalendarComponent, {
            width: '500px',
            height: 'auto',
            data: {
              incomingDateRange: new DateRange<Date>(this.startDate, this.endDate),
            },
          })
          .subscribe((range: DateRange<Date>) => {
            if (range) {
              this.startDate = new Date(moment(range.start).format('MM/DD/YYYY'));
              this.endDate = new Date(moment(range.end).format('MM/DD/YYYY'));
              this.startDateToDisplay = moment(range.start).format('MM/DD/YYYY');
              this.endDateToDisplay = moment(range.end).format('MM/DD/YYYY');
              this.fetchTransaction();
            }
          });
      } else {
        this.getFilter();
        let rangePeriod: {
          startDate: Date;
          endDate: Date;
        } = this.services.utilsService.giveRangeOfPeriod(this.period);
        this.startDate = new Date(moment(rangePeriod.startDate).format('MM/DD/YYYY'));
        this.endDate = new Date(moment(rangePeriod.endDate).format('MM/DD/YYYY'));
        this.startDateToDisplay = moment(rangePeriod.startDate).format('MM/DD/YYYY');
        this.endDateToDisplay = moment(rangePeriod.endDate).format('MM/DD/YYYY');
        this.fetchTransaction();
      }
    }
  }

  getTranslations(): void {
    this.translateService.get(this.translationKeys).subscribe((translations) => {
      this.translations.thisMonth = translations['admin-fields.month'];
      this.translations.thisYear = translations['admin-fields.year'];
      this.translations.thisWeek = translations['admin-fields.week'];
      this.translations.thisDay = translations['admin-fields.day'];
      this.translations.custom = translations['admin-fields.custom-period'];
      this.getFilter();
    });
  }

  public listenForLanguageChangeEvents(): void {
    this.services.eventService.subscribe('change:language', () => {
      this.getTranslations();
    });
  }

  getHcm(hcms: IHealthcarepackage[]) {
    if (hcms.length == 1) {
      return hcms[0].name;
    }
    let module = '';
    hcms.forEach((hcm, index) => {
      const virgule = index === hcms.length - 1 ? '' : ', ';
      module = module + hcm.name + virgule;
    });

    return module;
  }

  openDiscountModal(): void {
    this.services.modalService
      .openModal(DiscountComponent, {
        width: '500px',
        height: 'auto',
      })
      .subscribe((action) => {});
  }

  convertXafXof(currency: string) {
    return makeCurrencytoShow(currency);
  }

  _toHealthCredit(amount: number, rate: number): number {
    return toHealthCredit(amount, rate);
  }

  _toMoney(amount: number): number {
    return toMoney(amount, this.currency ? this.currency.rate : 0);
  }
}
